import { z } from 'zod';
import type { RecordStatus } from '../enums';
import { listQuerySchema, objectId, optionalText, recordStatus } from './common';
import type { UserRef } from './users';

/** "Serialized Type" of the reference's Add Equipment form. */
export const EQUIPMENT_SERIALIZED_TYPES = ['Serialized', 'Nonserialized'] as const;

/**
 * Manage Equipment (reference PC679, "Equipment List"). Fields of the reference's "Add
 * Equipment" form (product inventory, appendix 12.8): Model, Equipment Type*, Serialized
 * Type*, Equipment Name*, Equipment Amount($)*, Equipment In Stock*, Status*. Its image,
 * its transaction and shipping codes and the serial numbers of serialized equipment are not
 * built. "Equipment In Stock" is the number entered: nothing counts it down, because whether
 * a purchase does so in the reference was not seen.
 * Equipment types (PC683) and equipment status names (PC727) are managed lists (`lookups`).
 */
export const equipmentInputSchema = z.object({
  name: z.string().trim().min(1, 'Equipment Name is required').max(100),
  typeId: objectId,
  /** The product model this equipment is, when it is a phone or SIM of the catalogue. */
  modelId: objectId.nullable().optional().default(null),
  serializedType: z.enum(EQUIPMENT_SERIALIZED_TYPES).default('Nonserialized'),
  /** "Equipment Amount($)": six characters at most on the reference form. */
  priceCents: z
    .number({ invalid_type_error: 'Enter a valid amount' })
    .int()
    .min(0)
    .max(99_999_999)
    .default(0),
  /** "Equipment In Stock": five digits at most. */
  inStock: z
    .number({ invalid_type_error: 'Equipment In Stock is required' })
    .int()
    .min(0)
    .max(99_999)
    .default(0),
  status: recordStatus.default('Active'),
});
export type EquipmentInput = z.input<typeof equipmentInputSchema>;
export const equipmentUpdateSchema = equipmentInputSchema.partial();

export const equipmentListQuerySchema = listQuerySchema.extend({
  q: optionalText(),
  typeId: objectId.optional(),
  modelId: objectId.optional(),
  status: recordStatus.optional(),
});

export interface EquipmentDto {
  id: string;
  /** Image 1, when set (slot 1). */
  image: { slot: number; updatedAt: string } | null;
  name: string;
  type: { id: string; name: string } | null;
  model: { id: string; label: string } | null;
  serializedType: (typeof EQUIPMENT_SERIALIZED_TYPES)[number];
  priceCents: number;
  inStock: number;
  status: RecordStatus;
  createdAt: string;
  createdBy: UserRef | null;
  updatedAt: string;
  updatedBy: UserRef | null;
}
